import { FOUNDATIONS } from '../designFoundations.js';
import { PALETTE } from '../designPalette.js';
import { PIN_MARKER_HALF_BOX_PX } from '../elementPinAnchorPosition.js';
export { elementPinAnchorPosition, PIN_MARKER_HALF_BOX_PX } from '../elementPinAnchorPosition.js';
/** The existing extension/Team Review numbered pin face, shared with native streams.
 * Position, severity and animation remain with each surface's owning flow. */
export const ISSUE_PIN_FACE_STYLE = {
    // ds-hand-rolled: the shipped extension and Team Review pin is 25px; foundations have no pin-size token. Preserve its established geometry.
    width: '25px', height: '25px',
    borderRadius: FOUNDATIONS.radius.full,
    backgroundColor: `var(--oq-pin-color, ${PALETTE.brand.primary})`,
    color: PALETTE.surface.card,
    fontFamily: FOUNDATIONS.fontFamily.system, fontSize: FOUNDATIONS.fontSize[14],
    fontWeight: FOUNDATIONS.fontWeight.bold, lineHeight: FOUNDATIONS.lineHeight.solid,
    letterSpacing: FOUNDATIONS.letterSpacing.normal,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: `${FOUNDATIONS.space.half} solid ${PALETTE.surface.card}`,
    boxShadow: FOUNDATIONS.shadow.popover,
};
export const ISSUE_PIN_FACE_CSS = Object.entries(ISSUE_PIN_FACE_STYLE).map(([key, value]) => `${key.replace(/[A-Z]/g, match => '-' + match.toLowerCase())}:${value}`).join(';');
/** Screenshot reports keep the shared face at its readable size while the
 * image scales. CSS resolves the same document-edge clamp after every layout;
 * only the displayed centre changes, never the saved capture coordinates. */
export const SCREENSHOT_ISSUE_PIN_CSS = `${ISSUE_PIN_FACE_CSS};box-sizing:border-box;position:absolute;transform:translate(-50%,-50%);padding:0;cursor:pointer;appearance:none;left:clamp(${PIN_MARKER_HALF_BOX_PX}px,var(--oq-pin-x),calc(100% - ${PIN_MARKER_HALF_BOX_PX}px));top:clamp(${PIN_MARKER_HALF_BOX_PX}px,var(--oq-pin-y),calc(100% - ${PIN_MARKER_HALF_BOX_PX}px));`;
/** Actual report selectors, also used by the live design-system specimen. */
export const REPORT_PIN_CSS = `
.review-portal__pin{${SCREENSHOT_ISSUE_PIN_CSS}z-index:2}
.review-portal__pin:hover{box-shadow:${FOUNDATIONS.shadow.hover}}
.review-portal__pin:focus-visible{outline:${FOUNDATIONS.space.half} solid ${PALETTE.primary[700]};outline-offset:calc(-1 * ${FOUNDATIONS.space.half})}
.review-portal__pin--active{background:${PALETTE.brand.hover};box-shadow:${FOUNDATIONS.shadow.hover};z-index:3}
`;
export const REPORT_SCREENSHOT_CSS = `.review-portal__screenshot-wrap{position:relative}
.review-portal__screenshot-img{display:block;width:100%;height:auto}`;
export function screenshotIssuePinPositionStyle(xPercent, yPercent) {
    return `--oq-pin-x:${xPercent.toFixed(2)}%;--oq-pin-y:${yPercent.toFixed(2)}%`;
}
/** Custom properties let the extension keep selected/warning/animation rules
 * in CSS rather than overriding their state styles with React inline values. */
export const ISSUE_PIN_FACE_VARIABLES = Object.fromEntries(Object.entries(ISSUE_PIN_FACE_STYLE).map(([key, value]) => [
    '--oq-pin-face-' + key.replace(/[A-Z]/g, match => '-' + match.toLowerCase()), value,
]));
export const CAPTURE_HOVER_CSS = `position:fixed;box-sizing:border-box;pointer-events:none;outline:${FOUNDATIONS.space.half} solid ${PALETTE.brand.primary};border-radius:${FOUNDATIONS.radius.micro};`;
/** Runs inside the reviewed document, so viewer chrome/letterboxing scales
 * the target and annotation together. Self-contained for bridge injection. */
export function mountNativeCaptureMarkers(doc, faceCss, hoverCss, find, selected, anchor) {
    const root = doc.createElement('div');
    root.setAttribute('data-overlayqa', 'native-capture-markers');
    root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:2147483645;';
    const shadow = root.attachShadow({ mode: 'open' });
    const hover = doc.createElement('div');
    hover.setAttribute('data-testid', 'native-capture-hover');
    hover.style.cssText = hoverCss;
    hover.hidden = true;
    shadow.appendChild(hover);
    doc.documentElement.appendChild(root);
    let hovered = null;
    let pins = [];
    let frame = null;
    const win = doc.defaultView;
    const nodes = new Map();
    const draw = () => {
        frame = null;
        const rect = hovered?.isConnected ? hovered.getBoundingClientRect() : null;
        hover.hidden = !rect || !rect.width || !rect.height;
        if (rect)
            Object.assign(hover.style, { left: rect.x + 'px', top: rect.y + 'px', width: rect.width + 'px', height: rect.height + 'px' });
        for (const pin of pins) {
            let target = null;
            try {
                target = find(pin.selector);
            }
            catch { /* invalid/stale selector */ }
            const box = target?.getBoundingClientRect();
            let button = nodes.get(pin.id);
            if (!button) {
                button = doc.createElement('button');
                button.type = 'button';
                button.className = 'rb-pin';
                button.dataset.issueId = pin.id;
                button.style.cssText = faceCss + ';position:fixed;transform:translate(-50%,-50%);pointer-events:auto;cursor:pointer;padding:0;';
                button.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); selected(pin.id); });
                shadow.appendChild(button);
                nodes.set(pin.id, button);
            }
            button.hidden = !box || !box.width || !box.height;
            button.textContent = String(pin.number);
            button.setAttribute('aria-label', `Issue ${pin.number}: ${pin.title}`);
            if (box) {
                const position = anchor({ rect: box, layout: { scroll: { x: win.scrollX, y: win.scrollY },
                        document: { width: doc.documentElement.scrollWidth, height: doc.documentElement.scrollHeight } } });
                Object.assign(button.style, { left: position.x + 'px', top: position.y + 'px' });
            }
            // Explicit display is necessary: the shared face's display:flex wins
            // over the browser's weak [hidden] rule on a styled button.
            button.style.display = button.hidden ? 'none' : 'flex';
        }
        for (const [id, node] of nodes)
            if (!pins.some(pin => pin.id === id)) {
                node.remove();
                nodes.delete(id);
            }
    };
    const schedule = () => { if (frame === null)
        frame = win.requestAnimationFrame(draw); };
    win.addEventListener('scroll', schedule, true);
    win.addEventListener('resize', schedule);
    doc.addEventListener('load', schedule, true);
    const resize = typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(schedule) : null;
    resize?.observe(doc.documentElement);
    const observer = new win.MutationObserver(records => {
        if (records.some(record => record.target !== root && !root.contains(record.target)))
            schedule();
    });
    observer.observe(doc.documentElement, { subtree: true, childList: true, attributes: true, characterData: true });
    return {
        hover(element) { hovered = element; schedule(); },
        setPins(entries) { pins = entries; schedule(); },
        destroy() { observer.disconnect(); resize?.disconnect(); doc.removeEventListener('load', schedule, true); win.removeEventListener('scroll', schedule, true); win.removeEventListener('resize', schedule); if (frame !== null)
            win.cancelAnimationFrame(frame); root.remove(); },
    };
}
//# sourceMappingURL=issuePin.js.map